import { StyleSheet, View } from 'react-native';
import React from 'react';
import Animated, { useAnimatedStyle } from 'react-native-reanimated';
import { IPreviewCanvasModel } from 'src/store/_models/previewCanvas';
import { observer } from 'mobx-react-lite';
import { styles } from './CanvasBorders.styles';
import { useResponsiveScale } from 'src/contexts/ResponsiveScaleContext';
import { Image } from 'expo-image';
import TextLato from '@photosi/rn-components/text/text-lato/TextLato';
import { useStore } from 'src/contexts/StoreContext';
import Svg, { Rect } from 'react-native-svg';
import { neutrals } from 'src/theme/colors';

type CanvasBordersProps = {
  preview: IPreviewCanvasModel;
};

const CanvasBorders = observer(({ preview }: CanvasBordersProps) => {
  const canvasBorders = preview.userImageArea.userImage?.canvasBorders;
  const isEditing = preview.userImageArea.userImage?.isEditing;

  const { responsiveScaleSV } = useResponsiveScale();
  const { i18n } = useStore();

  // Helper function to scale objects with SharedValues
  const scaleObjectWithSV = (obj: any) => {
    'worklet';
    const result: any = {};
    for (const key in obj) {
      if (typeof obj[key] === 'number') {
        result[key] = obj[key] * responsiveScaleSV.value;
      } else {
        result[key] = obj[key];
      }
    }
    return result;
  };

  // Animated styles for border positions
  const borderStyles = {
    top: useAnimatedStyle(() => scaleObjectWithSV(preview.bordersPositions.top)),
    right: useAnimatedStyle(() => scaleObjectWithSV(preview.bordersPositions.right)),
    bottom: useAnimatedStyle(() => scaleObjectWithSV(preview.bordersPositions.bottom)),
    left: useAnimatedStyle(() => scaleObjectWithSV(preview.bordersPositions.left)),
  };

  // Animated styles for text overlays with correct width for rotated texts
  const textStyles = {
    top: useAnimatedStyle(() => scaleObjectWithSV(preview.bordersPositions.top)),
    right: useAnimatedStyle(() => scaleObjectWithSV(preview.bordersPositions.right)),
    bottom: useAnimatedStyle(() => scaleObjectWithSV(preview.bordersPositions.bottom)),
    left: useAnimatedStyle(() => scaleObjectWithSV(preview.bordersPositions.left)),
  };

  // Animated styles for corner positions
  const cornerStyles = {
    topLeft: useAnimatedStyle(() => scaleObjectWithSV(preview.blankCornersPosition.topLeft)),
    topRight: useAnimatedStyle(() => scaleObjectWithSV(preview.blankCornersPosition.topRight)),
    bottomRight: useAnimatedStyle(() => scaleObjectWithSV(preview.blankCornersPosition.bottomRight)),
    bottomLeft: useAnimatedStyle(() => scaleObjectWithSV(preview.blankCornersPosition.bottomLeft)),
  };

  // Animated style for inner user image area
  const innerAreaStyle = useAnimatedStyle(() =>
    scaleObjectWithSV({
      width: preview.innerUserImageArea.width,
      height: preview.innerUserImageArea.height,
      top: preview.innerUserImageArea.y,
      left: preview.innerUserImageArea.x,
    }),
  );

  return (
    <View style={styles.container}>
      {/* CORNERS */}
      <Animated.View style={[styles.cornerBlank, cornerStyles.topLeft]} />
      <Animated.View style={[styles.cornerBlank, cornerStyles.topRight]} />
      <Animated.View style={[styles.cornerBlank, cornerStyles.bottomRight]} />
      <Animated.View style={[styles.cornerBlank, cornerStyles.bottomLeft]} />
      {/* IMAGES */}
      {canvasBorders && (
        <View style={isEditing && styles.fade}>
          <Animated.View style={[borderStyles.top, { position: 'absolute', overflow: 'hidden' }]}>
            <Image
              style={StyleSheet.absoluteFillObject}
              contentFit={'cover'}
              source={{ uri: canvasBorders.topBorder.image }}
            />
          </Animated.View>
          <Animated.View style={[borderStyles.right, { position: 'absolute', overflow: 'hidden' }]}>
            <Image
              style={StyleSheet.absoluteFillObject}
              contentFit={'cover'}
              source={{ uri: canvasBorders.rightBorder.image }}
            />
          </Animated.View>
          <Animated.View style={[borderStyles.bottom, { position: 'absolute', overflow: 'hidden' }]}>
            <Image
              style={StyleSheet.absoluteFillObject}
              contentFit={'cover'}
              source={{ uri: canvasBorders.bottomBorder.image }}
            />
          </Animated.View>
          <Animated.View style={[borderStyles.left, { position: 'absolute', overflow: 'hidden' }]}>
            <Image
              style={StyleSheet.absoluteFillObject}
              contentFit={'cover'}
              source={{ uri: canvasBorders.leftBorder.image }}
            />
          </Animated.View>
        </View>
      )}
      {/* TEXT OVERLAYS */}
      <Animated.View style={[textStyles.top, styles.borderOverlay]}>
        <TextLato style={styles.text}>{i18n.t('canvas:border-text-top')}</TextLato>
      </Animated.View>
      <Animated.View style={[textStyles.right, styles.borderOverlay]}>
        <TextLato style={[styles.text, { transform: [{ rotate: '90deg' }], width: 200 }]}>
          {i18n.t('canvas:border-text-right')}
        </TextLato>
      </Animated.View>
      <Animated.View style={[textStyles.bottom, styles.borderOverlay]}>
        <TextLato style={styles.text}>{i18n.t('canvas:border-text-bottom')}</TextLato>
      </Animated.View>
      <Animated.View style={[textStyles.left, styles.borderOverlay]}>
        <TextLato style={[styles.text, { transform: [{ rotate: '270deg' }], width: 200 }]}>
          {i18n.t('canvas:border-text-left')}
        </TextLato>
      </Animated.View>
      {/* DASHED OVERLAY */}
      <Animated.View style={[styles.borderDashedOverlay, innerAreaStyle]}>
        <Svg height="100%" width="100%" style={StyleSheet.absoluteFill}>
          <Rect
            width="100%"
            height="100%"
            stroke={neutrals.white}
            strokeWidth={3}
            strokeDasharray={'9.5, 7.5'}
            strokeLinejoin={'round'}
            fill="none"
          />
        </Svg>
      </Animated.View>
    </View>
  );
});

export default CanvasBorders;
